'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

const navLinks = [
  { label: 'Products', href: '/products' },
  { label: 'For Brands', href: '/for-brands' },
  { label: 'For Reviewers', href: '/for-reviewers' },
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/about' },
  { label: 'Support', href: '/support' },
  { label: 'Contact', href: '/contact' },
];

interface HeaderProps {
  onDownloadClick?: () => void;
}

export default function Header({ onDownloadClick }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const router = useRouter();
  const supabase = createClient();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', session.user.id)
          .maybeSingle();
        setUserRole(profile?.role ?? 'reviewer');
      } else {
        setUserRole(null);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', session.user.id)
          .maybeSingle();
        setUserRole(profile?.role ?? 'reviewer');
      } else {
        setUserRole(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const getDashboardHref = () => {
    if (userRole === 'seller') return '/seller/dashboard';
    if (userRole === 'admin') return '/admin/applications';
    return '/reviewer/dashboard';
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setMenuOpen(false);
    router.push('/');
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-border'
            : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <AppLogo size={34} />
            <span className="font-heading font-bold text-base text-foreground hidden sm:block tracking-tight">
              IndianReview<span className="text-primary">Club</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="nav-link-underline text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-muted"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Link
              href="/seller-onboarding/register"
              className="hidden md:block text-sm font-medium text-foreground border border-border px-3.5 py-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              List a Product
            </Link>
            {user ? (
              <Link
                href={getDashboardHref()}
                className="hidden md:block text-sm font-semibold text-primary border border-primary/20 bg-primary-container px-3.5 py-1.5 rounded-lg hover:bg-primary/10 transition-colors"
              >
                My Dashboard
              </Link>
            ) : (
              <Link
                href="/auth"
                className="hidden md:block text-sm font-semibold text-foreground border border-border px-3.5 py-1.5 rounded-lg hover:bg-muted transition-colors"
              >
                Sign In
              </Link>
            )}
            <button
              onClick={onDownloadClick}
              className="cta-gradient text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-90 transition-opacity hidden sm:block font-heading shadow-sm"
            >
              Download App
            </button>
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Icon name="Bars3Icon" size={22} className="text-foreground" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" onClick={() => setMenuOpen(false)}>
          <div
            className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-2">
                <AppLogo size={28} />
                <span className="font-heading font-bold text-sm text-foreground">
                  IndianReview<span className="text-primary">Club</span>
                </span>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Close menu"
              >
                <Icon name="XMarkIcon" size={20} className="text-foreground" />
              </button>
            </div>
            <nav className="flex flex-col p-4 gap-1 flex-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <div className="my-2 border-t border-border" />
              <Link
                href="/seller-onboarding/register"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                List a Product
              </Link>
              {user ? (
                <>
                  <Link
                    href={getDashboardHref()}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-primary bg-primary-container hover:bg-primary/10 transition-colors"
                  >
                    My Dashboard
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted transition-colors text-left"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  href="/auth"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                >
                  Sign In
                </Link>
              )}
            </nav>
            <div className="p-4 border-t border-border">
              <button
                onClick={() => { setMenuOpen(false); onDownloadClick?.(); }}
                className="w-full cta-gradient text-white text-sm font-semibold py-3 rounded-xl font-heading"
              >
                Download App
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}